import styled from "styled-components";
import { useId, useRef, useState } from "react";
import DatePicker from "./SingleDatePicker";
import PeriodDatePicker from "./PeriodDatePicker";
import { UilTimes } from "@iconscout/react-unicons";

const Container = styled.div<{ $shouldShowCalendar: "show" | "hide" }>`
  button + div {
    position: absolute;
    z-index: 2;
    ${({ $shouldShowCalendar }) =>
      $shouldShowCalendar === "show"
        ? "opacity: 1; visibility: visible;"
        : "opacity: 0; visibility: hidden;"};

    transition: opacity 0.15s ease-in-out, visibility 0.15s ease-in-out;
  }

  .errorContainer {
    position: relative;
  }

  p.error {
    position: absolute;
    color: var(--color-aux-danger-700);
    font-size: 10px;
    font-family: var(--font-paragraph-default);
  }
`;

const Subcontainer = styled.button<{
  $placeholder: string;
  $onError: "hasError" | "noError";
  $hasMandatoryStyle?: { marginLeft: string };
}>`
  position: relative;
  width: 280px;
  // height: max-content;
  height: 47px;
  padding: 14px 12px 14px 38px;

  font-family: var(--font-paragraph-default);

  border-radius: 8px;
  border: 1px solid
    ${({ $onError }) =>
      $onError === "hasError"
        ? `var(--color-aux-danger-700)`
        : `var(--color-dark-blue-300)`};

  background: var(--color-light-0);

  cursor: pointer;

  .times {
    color: var(--color-text-labels);
    width: 9px;
    height: 9px;
  }

  &:hover {
    background: var(--color-light-0);
  }

  p.date {
    font-size: 14px;
    color: var(--color-text-labels);
    &.active {
      color: var(--color-text-paragraph);
      text-align: left;
      // margin-left: 18px;
      margin-left: 8px;
      padding-top: 5px;
    }
  }

  .clear {
    position: absolute;
    right: 12px;

    top: 12px;

    color: var(--color-text-labels);
    width: 18px;
    height: 18px;
    font-size: 14px;

    cursor: pointer;
    outline: none;
  }

  &::before {
    content: "";
    position: absolute;
    font-family: "unicons";

    color: var(--color-text-labels);

    left: 0;
    top: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background: var(--color-light-100);
    border-radius: 8px 0 0 8px;
    width: 38px;
    height: 100%;
    font-size: 22px;
  }

  &::after {
    content: "${({ $placeholder }) => $placeholder}";
    position: absolute;
    top: 8px;
    // left: 55px;
    left: 47px;
    font-size: 9px;
    color: var(--color-text-labels);
  }

  .mandatory {
    color: var(--color-aux-danger-700);
    ${({ $placeholder, $hasMandatoryStyle }) =>
      $placeholder &&
      `position: absolute;` +
        `font-size: 9px;` +
        `top: 8px;` +
        `left: ${$hasMandatoryStyle?.marginLeft}`}
  }

  &:focus-within {
    border: 1px solid var(--color-main-blue);

    &::before {
      color: var(--color-text-paragraph);
    }
  }

  .clear:focus {
    border: 1px solid var(--color-main-blue);
  }
`;

type TCalendarBaseProps = {
  id?: string;
  placeholder?: string;
  className?: string;
  hasMandatoryStyle?: { marginLeft: string };
  onError?: { hasError: boolean; message: string };
  minDate?: string; // format: "dd/MM/yyyy"
  maxDate?: string; // format: "dd/MM/yyyy"
};

type TCalendarProps<V extends "single" | "period"> = V extends "single"
  ? TCalendarBaseProps & {
      variant?: "single";
      onSelect?: (selectDate: string) => void;
      filled?: string;
      setDate?: (
        setButtonSelectedDate: React.Dispatch<React.SetStateAction<string>>,
        setDatePickerSelectedDates: React.Dispatch<
          React.SetStateAction<{ startDate: string }>
        >
      ) => void;
    }
  : TCalendarBaseProps & {
      variant: "period";
      onSelect?: (selectDate: { startDate: string; endDate: string }) => void;
      filled?: { startDate: string; endDate: string };
      setDate?: never;
    };

const Calendar: React.FC<TCalendarProps<"period" | "single">> = ({
  id,
  placeholder,
  className,
  filled,
  onSelect,
  onError,
  setDate,
  variant = "single",
  hasMandatoryStyle,
  minDate,
  maxDate,
}) => {
  const [shouldShowCalendar, setShouldShowCalendar] = useState(false);

  // Single date state
  const [selectDate, setSelectDate] = useState(
    typeof filled === "string" ? filled : ""
  );

  // Period date state
  const [selectedPeriod, setSelectedPeriod] = useState<{
    startDate: string;
    endDate: string;
  }>(
    typeof filled === "object" && filled !== null
      ? filled
      : { startDate: "", endDate: "" }
  );

  const [calendarFilled, setCalendarFilled] = useState<
    { startDate: string; endDate: string } | undefined
  >(typeof filled === "object" && filled !== null ? filled : undefined);
  const [shouldClearCalendar, /* setShouldClearCalendar */] = useState(false);
  const setSelectedDatesRef = useRef<
    | React.Dispatch<
        React.SetStateAction<{ startDate: string; endDate: string }>
      >
    | undefined
  >();

  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const backwardButtonId = useId();
  const forwardButtonId = useId();
  const datePickerId = useId();

  const handleFocus = () => {
    setShouldShowCalendar(true);
  };

  const handleOnBlur = (e: React.FocusEvent<HTMLButtonElement, Element>) => {
    if (
      (e.relatedTarget?.id === backwardButtonId ||
        e.relatedTarget?.id === forwardButtonId) &&
      variant !== "period"
    ) {
      buttonRef.current?.focus();
      return;
    }
    // Prevent closing if period mode and endDate matches filled.endDate
    if (
      variant === "period" &&
      selectedPeriod.endDate.length > 0 &&
      filled &&
      typeof filled === "object" &&
      selectedPeriod.endDate === filled.endDate
    ) {
      // Do not close calendar
      return;
    }

    setShouldShowCalendar(false);
  };

  // Format for period display
  const formatPeriod = (start: string, end: string) => {
    if (start && end) return `${start} - ${end}`;
    if (start && !end) return `${start} - `;
    return "";
  };

  return (
    <Container
      className={className}
      $shouldShowCalendar={shouldShowCalendar ? "show" : "hide"}
    >
      <Subcontainer
        ref={buttonRef}
        $onError={onError && onError.hasError ? "hasError" : "noError"}
        id={id}
        role="button"
        style={
          selectedPeriod.endDate.length > 0
            ? {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }
            : {}
        }
        onClick={handleFocus}
        onBlur={handleOnBlur}
        $placeholder={
          variant === "single"
            ? selectDate.length > 0
              ? placeholder ?? ""
              : ""
            : placeholder ?? ""
        }
        $hasMandatoryStyle={hasMandatoryStyle}
      >
        <p
          key={selectedPeriod.startDate.length + selectedPeriod.endDate.length}
          className={
            variant === "single"
              ? selectDate.length > 0
                ? "date active"
                : "date"
              : selectedPeriod.startDate && selectedPeriod.endDate
              ? "date active"
              : "date"
          }
        >
          {variant === "single"
            ? selectDate.length > 0
              ? selectDate
              : placeholder
            : selectedPeriod.startDate
            ? formatPeriod(selectedPeriod.startDate, selectedPeriod.endDate)
            : ""}
          {hasMandatoryStyle && <span className="mandatory">&nbsp;*</span>}
        </p>
        {selectedPeriod.endDate.length > 0 && (
          <button
            onClick={() => {
              setSelectedPeriod({ startDate: "", endDate: "" });
              setCalendarFilled({ startDate: "", endDate: "" });
              if (onSelect)
                (
                  onSelect as (date: {
                    startDate: string;
                    endDate: string;
                  }) => void
                )({ startDate: "", endDate: "" });
            }}
          >
            <UilTimes className="clear" />
          </button>
        )}
      </Subcontainer>
      {variant === "single" ? (
        <DatePicker
          id={datePickerId}
          onSelect={({ startDate }) => {
            setSelectDate(startDate);
            if (onSelect) (onSelect as (date: string) => void)(startDate);
          }}
          backwardButtonId={backwardButtonId}
          forwardButtonId={forwardButtonId}
          setDate={(setDatePickerSelectedDates) => {
            if (setDate) setDate(setSelectDate, setDatePickerSelectedDates);
          }}
        />
      ) : (
        <PeriodDatePicker
          key={
            (filled &&
            typeof filled === "object" &&
            filled.startDate &&
            filled.endDate
              ? "filled"
              : "") +
            " - " +
            shouldClearCalendar +
            (calendarFilled
              ? calendarFilled.startDate + calendarFilled.endDate
              : "") +
            "-calendar-datepicker"
          }
          setSelectedDatesRef={setSelectedDatesRef}
          id={datePickerId}
          onSelect={(result, /* setSelectedDates */) => {
            // If endDate is already set, clear before setting new dates
            if (selectedPeriod.endDate.length > 0) {
              setCalendarFilled({ startDate: "", endDate: "" });
              setSelectedPeriod({ startDate: "", endDate: "" });
              if (setSelectedDatesRef.current) {
                setSelectedDatesRef.current({ startDate: "", endDate: "" });
              }
              if (onSelect)
                (
                  onSelect as (date: {
                    startDate: string;
                    endDate: string;
                  }) => void
                )({ startDate: "", endDate: "" });
              // Do NOT close the calendar here
              return;
            }
            setCalendarFilled({
              startDate: result.startDate,
              endDate: result.endDate,
            });
            if (setSelectedDatesRef.current) {
              setSelectedDatesRef.current({
                startDate: result.startDate,
                endDate: result.endDate,
              });
            }
            setSelectedPeriod({
              startDate: result.startDate,
              endDate: result.endDate,
            });

            setShouldShowCalendar(false);

            if (onSelect)
              (
                onSelect as (date: {
                  startDate: string;
                  endDate: string;
                }) => void
              )(result);
          }}
          shouldClearSelected={shouldClearCalendar}
          filled={calendarFilled}
          minDate={minDate}
          maxDate={maxDate}
        />
      )}
      {onError?.hasError && (
        <div className="errorContainer">
          <p className="error">{onError.message}</p>
        </div>
      )}
    </Container>
  );
};

export default Calendar;
